import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPalette,
  faBoxArchive,
  faInbox,
  faEnvelopeOpen,
  faEnvelope,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {
  useGetInboxQuery,
  useArchiveConversationMutation,
  useBulkActionMutation,
  useGetUnreadCountQuery,
} from 'store/services/message/messageApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { useIsElite } from 'hooks/useIsElite';
import { RootState } from 'store';
import { AssetUrls } from 'config/assets';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import BubbleColorPickerModal from 'components/BubbleColorPickerModal/BubbleColorPickerModal';
import MagicButton from 'components/MagicButton/MagicButton';
import InboxStatusTabs, { InboxTab } from './components/InboxStatusTabs';
import { BulkAction } from './components/InboxActionBar';
import './InboxPage.css';

const stripHTML = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export default function InboxPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<InboxTab>('inbox');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastClickedIdRef = useRef<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    threadId: string;
    x: number;
    y: number;
  } | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showBubbleColorModal, setShowBubbleColorModal] = useState(false);

  const { data: meData } = useGetMeQuery();
  const { isElite } = useIsElite();
  // Redux state for rate limiting, maintenance mode, and socket connection
  const isRateLimited = useSelector((state: RootState) => state.rateLimit.isRateLimited);
  const isMaintenanceMode = useSelector((state: RootState) => state.maintenance.isMaintenanceModeActive);
  const isSocketConnected = useSelector((state: RootState) => state.message.isConnected);

  // Socket-based polling: Use WebSocket when connected, fallback to 60s polling when disconnected
  // When socket is connected, inbox updates in real-time via message:new events
  const pollingInterval = (isRateLimited || isMaintenanceMode)
    ? 0
    : isSocketConnected ? 0 : 60000; // No polling when connected, 60s fallback when disconnected

  const { data, isLoading, error } = useGetInboxQuery(
    {
      page,
      pageSize: 25,
      search: debouncedSearch || undefined,
      archived: activeTab === 'archived',
    },
    {
      pollingInterval,
      refetchOnMountOrArgChange: true,
    }
  );

  // Archived tab badge: total count of archived threads (lightweight pageSize=1 query)
  const { data: archivedCountData } = useGetInboxQuery(
    { page: 1, pageSize: 1, archived: true },
    { refetchOnMountOrArgChange: true }
  );
  const archivedCount = archivedCountData?.pagination?.total ?? 0;

  const { data: unreadData } = useGetUnreadCountQuery();
  const unreadCount = unreadData?.count ?? 0;

  const [archiveConversation] = useArchiveConversationMutation();
  const [bulkAction, { isLoading: isBulkLoading }] = useBulkActionMutation();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Reset pagination and clear selection when switching tabs
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
    lastClickedIdRef.current = null;
  }, [activeTab]);

  // Dismiss context menu on outside mousedown, scroll, Escape, or window resize
  useEffect(() => {
    if (!contextMenu) return;

    const dismiss = () => setContextMenu(null);
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest('.inbox-context-menu')) return;
      dismiss();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
      document.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  const handleArchive = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await archiveConversation(conversationId).unwrap();
      toast.success('Conversation archived');
    } catch (error) {
      toast.error('Failed to archive conversation');
    }
  };

  const handleCheckboxClick = (
    conversationId: string,
    e: React.MouseEvent<HTMLInputElement>
  ) => {
    e.stopPropagation();

    const isShift = e.shiftKey;
    const conversations = filteredConversations ?? [];
    const clickedIndex = conversations.findIndex(c => c.conversationId === conversationId);
    const anchorId = lastClickedIdRef.current;
    const anchorIndex = anchorId
      ? conversations.findIndex(c => c.conversationId === anchorId)
      : -1;

    setSelectedIds(prev => {
      const next = new Set(prev);

      if (isShift && anchorIndex !== -1 && clickedIndex !== -1) {
        const start = Math.min(anchorIndex, clickedIndex);
        const end = Math.max(anchorIndex, clickedIndex);
        for (let i = start; i <= end; i++) {
          const id = conversations[i]?.conversationId;
          if (id) next.add(id);
        }
      } else {
        // Normal click and Ctrl/Cmd+Click both toggle a single row
        if (next.has(conversationId)) next.delete(conversationId);
        else next.add(conversationId);
      }

      return next;
    });

    lastClickedIdRef.current = conversationId;
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const visibleIds = (filteredConversations ?? []).map(c => c.conversationId);
      const allSelected = visibleIds.length > 0 && visibleIds.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visibleIds.forEach(id => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const performBulkAction = async (
    threadIds: string[],
    action: BulkAction,
    opts: { clearSelectionOnSuccess?: boolean } = {}
  ) => {
    if (threadIds.length === 0) return;

    if (action === 'report_spam') {
      const confirmed = window.confirm(
        `Report ${threadIds.length} conversation${threadIds.length === 1 ? '' : 's'} as spam? ` +
        'The thread will be archived and our team will be notified.'
      );
      if (!confirmed) return;
    }

    try {
      const result = await bulkAction({ threadIds, action }).unwrap();
      const n = result.affected ?? threadIds.length;
      const messages: Record<BulkAction, string> = {
        archive: `Archived ${n} conversation${n === 1 ? '' : 's'}`,
        unarchive: `Moved ${n} conversation${n === 1 ? '' : 's'} to Inbox`,
        mark_read: `Marked ${n} conversation${n === 1 ? '' : 's'} as read`,
        mark_unread: `Marked ${n} conversation${n === 1 ? '' : 's'} as unread`,
        report_spam: `Reported ${n} conversation${n === 1 ? '' : 's'} as spam`,
      };
      toast.success(messages[action]);
      if (opts.clearSelectionOnSuccess) {
        setSelectedIds(new Set());
      }
    } catch (err) {
      toast.error('Bulk action failed. Please try again.');
    }
  };

  const handleBulkAction = (action: BulkAction) =>
    performBulkAction(Array.from(selectedIds), action, { clearSelectionOnSuccess: true });

  const handleRowContextMenu = (threadId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ threadId, x: e.clientX, y: e.clientY });
  };

  const handleContextMenuAction = async (action: BulkAction) => {
    if (!contextMenu) return;
    const { threadId } = contextMenu;
    setContextMenu(null);
    const isMultiSelect = selectedIds.has(threadId) && selectedIds.size > 1;
    const targetIds = isMultiSelect ? Array.from(selectedIds) : [threadId];
    await performBulkAction(targetIds, action, { clearSelectionOnSuccess: isMultiSelect });
  };

  const formatReceivedTime = (timestamp: string): string => {
    const messageDate = new Date(timestamp);
    const now = new Date();
    const diffHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);

    if (diffHours < 24) {
      return messageDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } else {
      return messageDate.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    }
  };

  const handleEliteRedirect = () => {
    navigate('/elite?source=bubble-colors');
  };

  // Search and archived filter are handled server-side
  const filteredConversations = data?.conversations;

  const visibleIds = (filteredConversations ?? []).map(c => c.conversationId);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));

  if (isLoading) {
    return (
      <div className="inbox-page">
        <div className="inbox-header">
          <h1>Inbox</h1>
          <p className="inbox-subheader">
            Read and respond to messages sent to you by other traders in the community.
          </p>
        </div>
        <div className="inbox-loading">Loading messages...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inbox-page">
        <div className="inbox-header">
          <h1>Inbox</h1>
        </div>
        <div className="inbox-error">Failed to load messages. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="inbox-page">
      <div className="inbox-header">
        <h1>Inbox</h1>
        <p className="inbox-subheader">
          Read and respond to messages sent to you by other traders in the community.
        </p>
      </div>

      <InboxStatusTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        unreadCount={unreadCount}
        archivedCount={archivedCount}
      />

      <div className="inbox-filters">
        <div className="inbox-search-container">
          <input
            type="text"
            placeholder="Search username or card name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="inbox-search"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="clear-search-button"
            >
              ×
            </button>
          )}
        </div>

        {selectedIds.size > 0 && (
          <div className="inbox-inline-actions">
            <span className="inbox-inline-actions-count">{selectedIds.size} selected</span>
            {activeTab === 'archived' ? (
              <button
                type="button"
                className="inbox-inline-action-button"
                disabled={isBulkLoading}
                onClick={() => handleBulkAction('unarchive')}
                title="Move to Inbox"
              >
                <FontAwesomeIcon icon={faInbox} />
              </button>
            ) : (
              <button
                type="button"
                className="inbox-inline-action-button"
                disabled={isBulkLoading}
                onClick={() => handleBulkAction('archive')}
                title="Archive"
              >
                <FontAwesomeIcon icon={faBoxArchive} />
              </button>
            )}
            <button
              type="button"
              className="inbox-inline-action-button"
              disabled={isBulkLoading}
              onClick={() => handleBulkAction('mark_read')}
              title="Mark as Read"
            >
              <FontAwesomeIcon icon={faEnvelopeOpen} />
            </button>
            <button
              type="button"
              className="inbox-inline-action-button"
              disabled={isBulkLoading}
              onClick={() => handleBulkAction('mark_unread')}
              title="Mark as Unread"
            >
              <FontAwesomeIcon icon={faEnvelope} />
            </button>
            <button
              type="button"
              className="inbox-inline-action-button inbox-inline-action-button--danger"
              disabled={isBulkLoading}
              onClick={() => handleBulkAction('report_spam')}
              title="Report Spam"
            >
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </button>
          </div>
        )}

        <button
          className="inbox-customize-button"
          onClick={() => setShowBubbleColorModal(true)}
          title="Customize your chat style"
          type="button"
        >
          <svg width="0" height="0" style={{ position: 'absolute' }}>
            <defs>
              <linearGradient id="palette-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6F51E4" />
                <stop offset="100%" stopColor="#E02CDC" />
              </linearGradient>
            </defs>
          </svg>
          <FontAwesomeIcon icon={faPalette} />
        </button>
      </div>

      {!filteredConversations || filteredConversations.length === 0 ? (
        <div className="inbox-empty">
          {debouncedSearch ? (
            <>
              <p>No messages match your search</p>
              <button onClick={() => setSearchQuery('')} className="clear-search-button-large">
                Clear search
              </button>
            </>
          ) : (
            <div className="inbox-empty-state">
              <img
                src={AssetUrls.logos.arevakhachIcon}
                alt="TradeMagic"
                className="inbox-empty-state-logo"
              />
              {activeTab === 'archived' ? (
                <div className="inbox-empty-state-title">Nothing archived yet.</div>
              ) : (
                <>
                  <div className="inbox-empty-state-title">Your inbox is quiet.</div>
                  <div className="inbox-empty-state-message">
                    Start a trade and it won&apos;t be for long.
                  </div>
                  <div className="inbox-empty-state-button">
                    <MagicButton
                      label="Find Cards to Send"
                      color="purple"
                      onPress={() => navigate('/sending')}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="inbox-table">
            <div className="inbox-table-header">
              <span className="col-select">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                  aria-label="Select all"
                />
              </span>
              <span className="col-username">Username</span>
              <span className="col-message">Message</span>
              <span className="col-received">Received</span>
              <span className="col-archive"></span>
            </div>

            {filteredConversations.map((conv) => (
              <div
                key={conv.conversationId}
                className={`inbox-row ${conv.unreadCount > 0 ? 'unread' : ''} ${selectedIds.has(conv.conversationId) ? 'selected' : ''}`}
                onClick={() => navigate(`/inbox/${conv.conversationId}`)}
                onContextMenu={(e) => handleRowContextMenu(conv.conversationId, e)}
              >
                <div className="col-select" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(conv.conversationId)}
                    onClick={(e) => handleCheckboxClick(conv.conversationId, e)}
                    onChange={() => { /* handled in onClick */ }}
                    aria-label={`Select conversation with ${conv.otherUser.username}`}
                  />
                </div>
                <div className="col-username">
                  <AvatarWithFlag
                    avatarUrl={conv.otherUser.avatarUrl}
                    username={conv.otherUser.username}
                    countryCode={conv.otherUser.country}
                    size={32}
                    isElite={conv.otherUser.isElite}
                    showAnimatedBorder={conv.otherUser.showAnimatedBorder}
                    borderColor1={conv.otherUser.borderColor1}
                    borderColor2={conv.otherUser.borderColor2}
                  />
                  <UsernameLink
                    username={conv.otherUser.username}
                    isElite={conv.otherUser.isElite}
                    className="inbox-username inbox-username--link"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {conv.unreadCount > 0 && (
                    <span className="inbox-unread-count">{conv.unreadCount}</span>
                  )}
                </div>
                <div className="col-message">{stripHTML(conv.lastMessage)}</div>
                <div className="col-received">{formatReceivedTime(conv.lastMessageAt)}</div>
                <div className="col-archive">
                  <button
                    className="archive-button"
                    onClick={(e) => handleArchive(conv.conversationId, e)}
                    title="Archive conversation"
                  >
                    <img
                      src={AssetUrls.icons.dropdowns.close}
                      alt="Archive"
                      style={{ width: 16, height: 16 }}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {data?.pagination && (
            <div className="inbox-pagination">
              <button
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
                className="pagination-button"
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {page} of {Math.ceil(data.pagination.total / data.pagination.pageSize)}
              </span>
              <button
                disabled={!data.pagination.hasMore}
                onClick={() => setPage(p => p + 1)}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
      {contextMenu && (() => {
        const isMultiSelectAction =
          selectedIds.has(contextMenu.threadId) && selectedIds.size > 1;
        const MENU_WIDTH = 200;
        const MENU_HEIGHT = isMultiSelectAction ? 210 : 180;
        const margin = 8;
        const left = Math.min(contextMenu.x, window.innerWidth - MENU_WIDTH - margin);
        const top = Math.min(contextMenu.y, window.innerHeight - MENU_HEIGHT - margin);
        const isArchivedTab = activeTab === 'archived';
        return (
          <div
            className="inbox-context-menu"
            style={{ top: Math.max(margin, top), left: Math.max(margin, left) }}
            role="menu"
          >
            {isMultiSelectAction && (
              <div className="inbox-context-menu-subtitle">
                {selectedIds.size} threads selected
              </div>
            )}
            <button
              type="button"
              className="inbox-context-menu-item"
              onClick={() => handleContextMenuAction(isArchivedTab ? 'unarchive' : 'archive')}
              role="menuitem"
            >
              <FontAwesomeIcon icon={isArchivedTab ? faInbox : faBoxArchive} />
              <span>{isArchivedTab ? 'Move to Inbox' : 'Archive'}</span>
            </button>
            <button
              type="button"
              className="inbox-context-menu-item"
              onClick={() => handleContextMenuAction('mark_read')}
              role="menuitem"
            >
              <FontAwesomeIcon icon={faEnvelopeOpen} />
              <span>Mark as Read</span>
            </button>
            <button
              type="button"
              className="inbox-context-menu-item"
              onClick={() => handleContextMenuAction('mark_unread')}
              role="menuitem"
            >
              <FontAwesomeIcon icon={faEnvelope} />
              <span>Mark as Unread</span>
            </button>
            <div className="inbox-context-menu-separator" />
            <button
              type="button"
              className="inbox-context-menu-item inbox-context-menu-item--danger"
              onClick={() => handleContextMenuAction('report_spam')}
              role="menuitem"
            >
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>Report Spam</span>
            </button>
          </div>
        );
      })()}

      <BubbleColorPickerModal
        isOpen={showBubbleColorModal}
        onClose={() => setShowBubbleColorModal(false)}
        currentColor1={meData?.bubbleColor1 || '#8B5CF6'}
        currentColor2={meData?.bubbleColor2 || '#6D28D9'}
        onSaveOverride={!isElite ? handleEliteRedirect : undefined}
      />
    </div>
  );
}
